/**
 * Shopify API Client - Centralized fetch utilities
 *
 * Provides standardized methods for interacting with Shopify's Cart API
 * and other endpoints, with consistent error handling and configuration.
 *
 * @module ShopifyAPI
 * @version 1.0.0
 * @created 2025-10-18 (Phase 4 Wave 1 - Quick Wins)
 */

/**
 * Creates standard fetch configuration for Shopify API requests
 *
 * @param {string} [type='json'] - Response type ('json' or 'javascript')
 * @param {string} [method='POST'] - HTTP method
 * @returns {Object} Fetch configuration object with headers
 *
 * @example
 * const config = fetchConfig();
 * // Returns: { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
 */
function fetchConfig(type = 'json', method = 'POST') {
  return {
    method: method,
    headers: {
      'Content-Type': 'application/json',
      'Accept': `application/${type}`
    }
  };
}

/**
 * Shopify Cart API wrapper
 * Provides methods for common cart operations with consistent error handling
 */
const ShopifyCartAPI = {

  /**
   * Get current cart state
   *
   * @param {string} [sectionId='main-cart-items'] - Section ID for cart rendering
   * @returns {Promise<Response>} Fetch response
   *
   * @example
   * const cart = await ShopifyCartAPI.get().then(r => r.text());
   */
  get(sectionId = 'main-cart-items') {
    return fetch(`${window.routes?.cart_url || '/cart'}?section_id=${sectionId}`);
  },

  /**
   * Get cart as JSON
   *
   * @returns {Promise<Object>} Cart object
   *
   * @example
   * const cartData = await ShopifyCartAPI.getJSON();
   * console.log(cartData.item_count);
   */
  async getJSON() {
    const response = await fetch('/cart.js');
    return response.json();
  },

  /**
   * Add item(s) to cart
   *
   * @param {Object|Array} items - Item(s) to add { id, quantity, properties? }
   * @returns {Promise<Object>} Cart response
   *
   * @example
   * // Single item
   * await ShopifyCartAPI.add({ id: 123456, quantity: 1 });
   *
   * // Multiple items
   * await ShopifyCartAPI.add([
   *   { id: 123456, quantity: 1 },
   *   { id: 789012, quantity: 2 }
   * ]);
   */
  async add(items) {
    const body = Array.isArray(items)
      ? JSON.stringify({ items })
      : JSON.stringify({ items: [items] });

    const response = await fetch('/cart/add.js', {
      ...fetchConfig(),
      body
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }

    return response.json();
  },

  /**
   * Update cart item quantity
   *
   * @param {number|string} line - Line item index (1-based) or variant ID
   * @param {number} quantity - New quantity (0 to remove)
   * @param {Object} [options] - Additional options
   * @param {Array<string>} [options.sections] - Section IDs to return in response
   * @param {string} [options.sections_url] - URL for section rendering context
   * @returns {Promise<Object>} Updated cart (or Response if not JSON)
   *
   * @example
   * // Simple update
   * await ShopifyCartAPI.change(1, 3);
   *
   * // With sections for page updates
   * await ShopifyCartAPI.change(1, 2, {
   *   sections: ['cart-items', 'cart-footer'],
   *   sections_url: window.location.pathname
   * });
   */
  async change(line, quantity, options = {}) {
    const payload = { line, quantity, ...options };
    const body = JSON.stringify(payload);
    const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
      ...fetchConfig(),
      body
    });

    // Return response object if sections requested (text response)
    // Otherwise return parsed JSON (standard cart object)
    return options.sections ? response : response.json();
  },

  /**
   * Update multiple cart items at once
   *
   * @param {Object} updates - Object with line numbers as keys and quantities as values
   * @returns {Promise<Object>} Updated cart
   *
   * @example
   * await ShopifyCartAPI.update({ 1: 2, 2: 0, 3: 5 });
   */
  async update(updates) {
    const body = JSON.stringify({ updates });
    const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
      ...fetchConfig(),
      body
    });
    return response.json();
  },

  /**
   * Clear entire cart
   *
   * @returns {Promise<Object>} Empty cart object
   *
   * @example
   * await ShopifyCartAPI.clear();
   */
  async clear() {
    const response = await fetch('/cart/clear.js', {
      ...fetchConfig()
    });
    return response.json();
  },

  /**
   * Update cart note
   *
   * @param {string} note - Cart note text
   * @returns {Promise<Object>} Updated cart object
   *
   * @example
   * await ShopifyCartAPI.updateNote('Gift wrap please!');
   */
  async updateNote(note) {
    const body = JSON.stringify({ note });
    const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
      ...fetchConfig(),
      body
    });
    return response.json();
  }
};

/**
 * Shopify Product API wrapper
 */
const ShopifyProductAPI = {

  /**
   * Get product data as JSON
   *
   * @param {string} handle - Product handle
   * @returns {Promise<Object>} Product object
   *
   * @example
   * const product = await ShopifyProductAPI.get('my-product');
   * console.log(product.title, product.price);
   */
  async get(handle) {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) {
      throw new Error(`Product not found: ${handle}`);
    }
    return response.json();
  },

  /**
   * Get product recommendations
   *
   * @param {number} productId - Product ID
   * @param {number} [limit=4] - Number of recommendations
   * @returns {Promise<Array>} Array of recommended products
   *
   * @example
   * const recs = await ShopifyProductAPI.recommendations(123456, 4);
   */
  async recommendations(productId, limit = 4) {
    const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}`);
    const data = await response.json();
    return data.products || [];
  }
};

/**
 * Utility to handle section rendering
 * Fetches and parses section HTML from Shopify's section rendering endpoint
 *
 * @param {string} url - Section URL
 * @param {string} selector - CSS selector to extract from response
 * @returns {Promise<Element|null>} Parsed element or null
 *
 * @example
 * const cartItems = await fetchSection('/cart?section_id=cart-items', '#CartItems');
 * document.getElementById('cart').innerHTML = cartItems.innerHTML;
 */
async function fetchSection(url, selector) {
  const response = await fetch(url);
  const text = await response.text();
  const html = document.createElement('div');
  html.innerHTML = text;
  return html.querySelector(selector);
}

/**
 * Debounce function for optimizing frequent API calls
 *
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait
 * @returns {Function} Debounced function
 *
 * @example
 * const debouncedSearch = debounce((query) => {
 *   ShopifyProductAPI.search(query);
 * }, 300);
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Export to global scope for use across theme
window.ShopifyAPI = {
  Cart: ShopifyCartAPI,
  Product: ShopifyProductAPI,
  fetchConfig,
  fetchSection,
  debounce
};

// Also keep fetchConfig available globally for backwards compatibility
window.fetchConfig = fetchConfig;
